import { useEffect, useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VendorRechargeHistory from "../VendorRechargeHistory/VendorRechargeHistory";
import styles from "./VendorBalancePage.module.css";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const VendorBalancePage = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [proof, setProof] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isListVisible, setIsListVisible] = useState(false);
  const inputRef = useRef(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        if (permission !== "granted") {
          console.log("Notification permission denied");
        }
      });
    } else {
      console.log("Notifications not supported in this browser");
    }
  }, []);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setMessage("Error: No authentication token found");
          return;
        }

        const response = await fetch(
          `https://prod.toxboxai.com/api/owner/vendors?page=1&limit=100`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        if (data.success) {
          setVendors(data.data);
          if (!debouncedSearchTerm && isListVisible) {
            setFilteredVendors(data.data);
          }
        } else {
          setMessage("Error: Failed to fetch vendors");
        }
      } catch (error) {
        setMessage("Error fetching vendors: " + error.message);
      }
    };

    if (isListVisible && vendors.length === 0) {
      fetchVendors();
    }
  }, [isListVisible, vendors.length]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      const filtered = vendors.filter((vendor) =>
        vendor.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(isListVisible ? vendors : []);
    }
  }, [debouncedSearchTerm, vendors, isListVisible]);

  useEffect(() => {
    if (message) {
      if (message.includes("Error")) {
        toast.error(message, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }

      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Vendor Balance Update", {
          body: message,
          icon: "/path/to/icon.png",
        });
      }
    }
  }, [message]);

  const handleFileChange = (e) => {
    setProof(e.target.files[0]);
  };

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor._id);
    setSelectedVendorName(vendor.name);
    setSearchTerm(vendor.name);
    setIsListVisible(false);
    setFilteredVendors([]);
  };

  const handleInputFocus = () => {
    setIsListVisible(true);
    setSearchTerm("");
    setSelectedVendor("");
    setSelectedVendorName("");
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsListVisible(false);
      setFilteredVendors([]);
      if (selectedVendor) {
        setSearchTerm(selectedVendorName);
      }
    }, 200);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedVendor("");
    setSelectedVendorName("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMessage("Error: No authentication token found");
        setLoading(false);
        return;
      }

      const response = await fetch(
        "https://prod.toxboxai.com/api/recharge/vendors/recharge",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            vendorId: selectedVendor,
            amount: parseFloat(amount),
            notes,
          }),
        }
      );

      if (response.ok) {
        setMessage("Balance added successfully!");
        setAmount("");
        setNotes("");
        setSelectedVendor("");
        setSelectedVendorName("");
        setSearchTerm("");
        setProof(null);
        setFilteredVendors([]);
        setIsListVisible(false);
        setRefreshKey((prev) => prev + 1);
      } else {
        const errorData = await response.json();
        setMessage("Error: " + errorData.message);
      }
    } catch (error) {
      setMessage("Error: Please enter valid data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.heading}>Add Vendor Balance</h2>
        <p className={styles.subheading}>
          Record vendor transactions and update account balances.
        </p>
      </div>

      <div className={styles.formBox}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="searchVendor" className={styles.label}>
                Vendor
              </label>
              <input
                id="searchVendor"
                type="text"
                placeholder="Search vendors by name..."
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                ref={inputRef}
                className={styles.input}
              />
              {isListVisible && filteredVendors.length > 0 && (
                <ul className={styles.vendorList}>
                  {filteredVendors.map((vendor) => (
                    <li
                      key={vendor._id}
                      className={styles.vendorItem}
                      onClick={() => handleVendorSelect(vendor)}
                    >
                      {vendor.name} ({vendor.email})
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="amount" className={styles.label}>
                Credit Points
              </label>
              <input
                id="amount"
                type="number"
                placeholder="Enter points..."
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="notes" className={styles.label}>
                Remarks / Notes
              </label>
              <textarea
                id="notes"
                placeholder="Enter additional details..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={styles.textarea}
                rows="3"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="proof" className={styles.label}>
                Upload Proof (Optional)
              </label>
              <input
                id="proof"
                type="file"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="submit"
              disabled={loading || !selectedVendor || !amount}
              className={styles.saveButton}
            >
              {loading ? "Processing..." : "Save Balance"}
            </button>
            {/* <button
              type="button"
              className={styles.cancelButton}
              onClick={() => {
                setAmount("");
                setNotes("");
                setSelectedVendor("");
                setSelectedVendorName("");
                setSearchTerm("");
                setProof(null);
                setFilteredVendors([]);
                setIsListVisible(false);
              }}
            >
              Cancel
            </button> */}
          </div>
        </form>
      </div>

      {selectedVendor && (
        <div className={styles.historyWrapper}>
          <VendorRechargeHistory
            refreshKey={refreshKey}
            vendorId={selectedVendor}
          />
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default VendorBalancePage;